import { isAxiosError } from 'axios';
import {
  isModuleAccessDeniedError,
  MODULE_ACCESS_DENIED_USER_MESSAGE,
} from './moduleAccessDenied.ts';

const AXIOS_STATUS_MESSAGE = /^Request failed with status code \d+/i;

/**
 * User-visible message from failed requests. Prefer backend `{ error: string }`
 * (and common `message` / `detail`) over raw HTTP status text.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isModuleAccessDeniedError(error)) {
    return MODULE_ACCESS_DENIED_USER_MESSAGE;
  }
  if (isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string') {
      const t = data.trim();
      if (t) return t;
    }
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const o = data as Record<string, unknown>;
      if (typeof o.error === 'string' && o.error.trim()) return o.error.trim();
      if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();
      if (typeof o.detail === 'string' && o.detail.trim()) return o.detail.trim();
    }
    if (error.message && !AXIOS_STATUS_MESSAGE.test(error.message)) {
      return error.message.trim();
    }
    return fallback;
  }
  if (error instanceof Error && error.message && !AXIOS_STATUS_MESSAGE.test(error.message)) {
    return error.message;
  }
  return fallback;
}

/**
 * For existing error UI: on 403 only, show backend permission/message body.
 * All other statuses keep otherwiseMessage exactly as today.
 */
export function resolve403ErrorMessage(error: unknown, otherwiseMessage: string): string {
  if (isModuleAccessDeniedError(error)) {
    return MODULE_ACCESS_DENIED_USER_MESSAGE;
  }
  if (!isAxiosError(error) || error.response?.status !== 403) {
    return otherwiseMessage;
  }
  const data = error.response.data;
  if (typeof data === 'string') {
    const t = data.trim();
    if (t) return t;
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    if (typeof o.error === 'string' && o.error.trim()) return o.error.trim();
    if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();
    if (typeof o.detail === 'string' && o.detail.trim()) return o.detail.trim();
  }
  return otherwiseMessage;
}
